// components/landing/sections/HowItWorks.tsx
// ---------------------------------------------------------------------------
// 4-step process flow: Upload -> Extract -> Validate -> Analyze
// Replaces DataDimensions + PlatformDemo with a single conversion-oriented section.
// ---------------------------------------------------------------------------

import {
  QueryStats as AnalyzeIcon,
  AutoAwesome as ExtractIcon,
  SvgIconComponent,
  CloudUpload as UploadIcon,
  FactCheck as ValidateIcon,
} from "@mui/icons-material";
import { Box, Typography, alpha, useMediaQuery, useTheme } from "@mui/material";
import { athenaColors } from "../colors";
import { useTranslation } from "../../../i18n";

interface Step {
  icon: SvgIconComponent;
  number: string;
  title: string;
  description: string;
  accent: string;
}

export default function HowItWorks() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();

  const STEPS: Step[] = [
    {
      icon: UploadIcon,
      number: "01",
      title: t("landing.step_upload_title", "Upload"),
      description: t("landing.step_upload_desc", "Clinical documents, pathology reports, scans, voice notes, or bulk ZIP archives."),
      accent: athenaColors.orange,
    },
    {
      icon: ExtractIcon,
      number: "02",
      title: t("landing.step_extract_title", "Extract"),
      description: t("landing.step_extract_desc", "AI structures data across 44 cancer types: molecular, hematological, and cancer-specific fields."),
      accent: athenaColors.orange,
    },
    {
      icon: ValidateIcon,
      number: "03",
      title: t("landing.step_validate_title", "Validate"),
      description: t("landing.step_validate_desc", "Review, refine, and approve extracted tables with inline editing and audit trails."),
      accent: athenaColors.orange,
    },
    {
      icon: AnalyzeIcon,
      number: "04",
      title: t("landing.step_analyze_title", "Analyze"),
      description: t("landing.step_analyze_desc", "Dynamic dashboards, natural language queries, cohort analysis, and filtered data export."),
      accent: athenaColors.orange,
    },
  ];

  return (
    <Box
      sx={{
        py: { xs: 8, md: 10 },
        px: { xs: 3, md: 8 },
        background: athenaColors.deepNavy,
      }}
    >
      <Typography
        variant="h3"
        sx={{
          textAlign: "center",
          fontWeight: 700,
          color: athenaColors.white,
          mb: 2,
          fontSize: { xs: "1.6rem", md: "2rem" },
        }}
      >
        {t("landing.how_it_works_title", "How It Works")}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          textAlign: "center",
          color: athenaColors.lightPeriwinkle,
          mb: { xs: 5, md: 7 },
          maxWidth: 600,
          mx: "auto",
        }}
      >
        {t("landing.how_it_works_subtitle", "From unstructured clinical text to actionable intelligence in four steps.")}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "center", md: "stretch" },
          gap: { xs: 3, md: 0 },
          maxWidth: 1100,
          mx: "auto",
        }}
      >
        {STEPS.map((step, idx) => {
          const Icon = step.icon;
          const isLast = idx === STEPS.length - 1;

          return (
            <Box
              key={step.number}
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
                flex: 1,
              }}
            >
              {/* Step card */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  p: { xs: 2.5, md: 3 },
                  borderRadius: 3,
                  // Match the in-platform empty-dashboard "How It Works" cards
                  // (WelcomeHeroCard): subtle white glass instead of frosted light.
                  background: alpha(athenaColors.white, 0.08),
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${alpha(athenaColors.white, 0.12)}`,
                  transition: "transform 0.25s ease, box-shadow 0.25s ease",
                  width: { xs: 260, md: "100%" },
                  minHeight: { md: 220 },
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `0 12px 32px ${alpha(
                      theme.palette.primary.main,
                      0.12
                    )}`,
                  },
                }}
              >
                {/* Step number + icon */}
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: alpha(step.accent, 0.15),
                    border: `1px solid ${alpha(step.accent, 0.25)}`,
                    mb: 2,
                    position: "relative",
                  }}
                >
                  <Icon sx={{ fontSize: 28, color: step.accent }} />
                  <Typography
                    variant="caption"
                    sx={{
                      position: "absolute",
                      top: -8,
                      right: -8,
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      background: alpha(step.accent, 0.15),
                      color: theme.palette.common.white,
                      fontWeight: 700,
                      fontSize: "0.65rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {step.number}
                  </Typography>
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.common.white,
                    mb: 1,
                    fontSize: "1rem",
                  }}
                >
                  {step.title}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: athenaColors.lightPeriwinkle,
                    lineHeight: 1.5,
                    fontSize: "0.82rem",
                  }}
                >
                  {step.description}
                </Typography>
              </Box>

              {/* Arrow connector (not on last item) */}
              {!isLast && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: { md: 1 },
                    py: { xs: 0.5, md: 0 },
                    transform: {
                      xs: "rotate(90deg)",
                      md: "rotate(0deg)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 2,
                      background: `linear-gradient(90deg, ${alpha(
                        athenaColors.royalBlue,
                        0.4
                      )}, ${alpha(athenaColors.royalBlue, 0.6)})`,
                      borderRadius: 1,
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        right: -4,
                        top: -3,
                        width: 0,
                        height: 0,
                        borderTop: "4px solid transparent",
                        borderBottom: "4px solid transparent",
                        borderLeft: `6px solid ${alpha(
                          athenaColors.royalBlue,
                          0.6
                        )}`,
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
